/**
 * Правка личных сообщений бота.
 *
 * Формулировки живут в настройках сообщества, а не в коде: поменять слово в
 * сообщении не должно требовать выкладки. Здесь их видно, можно поправить,
 * выключить и вернуть к исходному виду.
 */

import { type ReactNode } from 'react';
import { NOTIFICATIONS, type NotificationSettings } from '@ak/shared';

import { Button, Checkbox, TextArea } from './ui.tsx';

export function NotificationEditor({
  value,
  onChange,
}: {
  value: NotificationSettings;
  onChange: (next: NotificationSettings) => void;
}): ReactNode {
  const patch = (id: keyof NotificationSettings, change: Partial<NotificationSettings[typeof id]>): void => {
    onChange({ ...value, [id]: { ...value[id], ...change } });
  };

  return (
    <div className="space-y-4">
      {NOTIFICATIONS.map((definition) => {
        const setting = value[definition.id];
        const isDefault = setting.template === definition.defaultTemplate;

        return (
          <div
            key={definition.id}
            className="rounded-xl p-4"
            style={{ background: 'var(--surface-raised)', border: '1px solid var(--border-subtle)' }}
          >
            <div className="flex flex-wrap items-start justify-between gap-3">
              <div className="min-w-0 flex-1 basis-64">
                <div className="font-medium">{definition.label}</div>
                <p className="mt-0.5 text-sm" style={{ color: 'var(--text-muted)' }}>
                  {definition.description}
                </p>
              </div>

              <label className="flex shrink-0 items-center gap-2">
                <Checkbox
                  checked={setting.enabled}
                  onChange={(checked) => {
                    patch(definition.id, { enabled: checked });
                  }}
                />
                <span className="text-sm">{setting.enabled ? 'Отправляется' : 'Выключено'}</span>
              </label>
            </div>

            <div className="mt-3">
              <TextArea
                rows={4}
                value={setting.template}
                disabled={!setting.enabled}
                onChange={(event) => {
                  patch(definition.id, { template: event.target.value });
                }}
              />
            </div>

            <div className="mt-2 flex flex-wrap items-center gap-x-3 gap-y-2">
              <span className="text-xs" style={{ color: 'var(--text-muted)' }}>
                Подстановки:
              </span>
              {definition.placeholders.map((placeholder) => (
                <span
                  key={placeholder.key}
                  title={placeholder.description}
                  className="rounded-md px-1.5 py-0.5 font-mono text-xs"
                  style={{ background: 'var(--surface-panel)', color: 'var(--accent)' }}
                >
                  {`{${placeholder.key}}`}
                </span>
              ))}

              <Button
                size="sm"
                variant="ghost"
                disabled={isDefault}
                title={isDefault ? 'Текст и так исходный' : 'Вернуть текст, который был до правок'}
                onClick={() => {
                  patch(definition.id, { template: definition.defaultTemplate });
                }}
              >
                Сбросить текст
              </Button>
            </div>

            {/* Строка с пустой подстановкой выпадает целиком — так же, как
                раньше пропадала ссылка у набора без сообщения. Об этом лучше
                сказать, чем оставить человека гадать, почему строки нет. */}
            <p className="mt-2 text-xs" style={{ color: 'var(--text-muted)' }}>
              Строка, где подстановка окажется пустой, не отправляется целиком.
            </p>
          </div>
        );
      })}
    </div>
  );
}
